// client/src/contexts/CookieConsentContext.tsx
"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Lazy-load js-cookie to avoid SSR failures
let _Cookies: typeof import('js-cookie').default | null = null;
const getCookiesLib = () => {
  if (!_Cookies && typeof window !== 'undefined') {
    // require() returns the ES module wrapper; the API is on .default.
    const lib = require('js-cookie');
    _Cookies = lib.default ?? lib;
  }
  return _Cookies;
};

// Cookie consent categories
export interface CookieConsentState {
  essential: boolean; // Always true - required for app functionality
  analytics: boolean; // Google Analytics and custom analytics
  marketing: boolean; // Marketing cookies and third-party tracking
}

// Consent metadata
export interface ConsentMetadata {
  version: string;
  timestamp: number;
  expiresAt: number;
  /** User id whose sign-in last auto-accepted analytics in this browser. */
  autoGrantedFor?: string;
}

// Full consent data structure
export interface CookieConsentData {
  consent: CookieConsentState;
  metadata: ConsentMetadata;
}

// Context type
interface CookieConsentContextType {
  consent: CookieConsentState;
  hasConsent: boolean;
  isLoading: boolean;
  updateConsent: (category: keyof CookieConsentState, value: boolean) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  resetConsent: () => void;
  hasGivenConsent: (category: keyof CookieConsentState) => boolean;
}

// Default consent state
const DEFAULT_CONSENT: CookieConsentState = {
  essential: true, // Essential cookies are always required
  analytics: false,
  marketing: false,
};

// Consent configuration
const CONSENT_CONFIG = {
  version: '1.0.0',
  durationDays: 365, // Consent expires after 1 year
  storageKey: 'delphina_cookie_consent',
};

/** Fired on window when consent is written outside the provider. */
export const CONSENT_CHANGED_EVENT = 'delphina:cookie-consent-changed';

function readStoredConsent(): CookieConsentData | null {
  try {
    const stored = localStorage.getItem(CONSENT_CONFIG.storageKey);
    return stored ? (JSON.parse(stored) as CookieConsentData) : null;
  } catch {
    return null;
  }
}

/** Persist a consent decision to localStorage and the server-readable cookies. */
function writeConsent(newConsent: CookieConsentState, autoGrantedFor?: string): void {
  const consentData: CookieConsentData = {
    consent: newConsent,
    metadata: {
      version: CONSENT_CONFIG.version,
      timestamp: Date.now(),
      expiresAt: Date.now() + (CONSENT_CONFIG.durationDays * 24 * 60 * 60 * 1000),
      ...(autoGrantedFor ? { autoGrantedFor } : {}),
    },
  };
  localStorage.setItem(CONSENT_CONFIG.storageKey, JSON.stringify(consentData));

  // Server-readable mirrors. Best effort: a cookie failure must never stop
  // the consent decision itself from being saved.
  try {
    const Cookies = getCookiesLib();
    const options = { expires: CONSENT_CONFIG.durationDays, sameSite: 'Lax' as const, secure: process.env.NODE_ENV === 'production' };
    Cookies?.set('cookie_consent_given', 'true', options);
    Object.entries(newConsent).forEach(([category, value]) => {
      Cookies?.set(`cookie_consent_${category}`, value.toString(), options);
    });
  } catch (error) {
    console.error('Error writing cookie consent cookies:', error);
  }
}

/**
 * Signing in or signing up accepts analytics cookies (Syd, 28 Oct 2026),
 * including over an earlier banner reject. Marketing is left as it was.
 * Runs once per user per browser: if that user later turns analytics off in
 * cookie settings, the choice sticks. Returns true when it changed consent.
 */
export function grantAnalyticsConsentForUser(userId: string): boolean {
  if (typeof window === 'undefined' || !userId) return false;
  const stored = readStoredConsent();
  if (stored?.metadata?.autoGrantedFor === userId) return false;
  try {
    writeConsent(
      { essential: true, analytics: true, marketing: stored?.consent?.marketing ?? false },
      userId,
    );
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
    return true;
  } catch (error) {
    console.error('Error saving cookie consent:', error);
    return false;
  }
}

// Create context
const CookieConsentContext = createContext<CookieConsentContextType | null>(null);

// Provider component
export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsentState>(DEFAULT_CONSENT);
  const [isLoading, setIsLoading] = useState(true);
  const [hasConsent, setHasConsent] = useState(false);

  // Load consent from localStorage on mount, and again whenever it is
  // written outside the provider (sign-in auto-accept).
  useEffect(() => {
    const load = () => {
    try {
      const storedConsent = localStorage.getItem(CONSENT_CONFIG.storageKey);
      
      if (storedConsent) {
        const consentData: CookieConsentData = JSON.parse(storedConsent);
        
        // Check if consent is still valid (not expired)
        if (consentData.metadata.expiresAt > Date.now()) {
          // Check if version matches current version
          if (consentData.metadata.version === CONSENT_CONFIG.version) {
            setConsent(consentData.consent);
            setHasConsent(true);
          } else {
            // Version mismatch - reset consent
            setConsent(DEFAULT_CONSENT);
            setHasConsent(false);
          }
        } else {
          // Consent expired - reset consent
          setConsent(DEFAULT_CONSENT);
          setHasConsent(false);
        }
      } else {
        // No stored consent - set default state
        setConsent(DEFAULT_CONSENT);
        setHasConsent(false);
      }
    } catch (error) {
      console.error('Error loading cookie consent:', error);
      setConsent(DEFAULT_CONSENT);
      setHasConsent(false);
    } finally {
      setIsLoading(false);
    }
    };
    load();
    window.addEventListener(CONSENT_CHANGED_EVENT, load);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, load);
  }, []);

  // Save consent to localStorage whenever it changes
  const saveConsent = (newConsent: CookieConsentState) => {
    try {
      // Keep the auto-accept marker so a later manual choice is not
      // overridden by the next sign-in.
      writeConsent(newConsent, readStoredConsent()?.metadata?.autoGrantedFor);
      setConsent(newConsent);
      setHasConsent(true);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  // Update individual consent category
  const updateConsent = (category: keyof CookieConsentState, value: boolean) => {
    const newConsent = { ...consent, [category]: value };
    saveConsent(newConsent);
  };

  // Accept all cookies
  const acceptAll = () => {
    const allConsent: CookieConsentState = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(allConsent);
  };

  // Reject all non-essential cookies
  const rejectAll = () => {
    const minimalConsent: CookieConsentState = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(minimalConsent);
  };

  // Reset consent (clear stored data)
  const resetConsent = () => {
    try {
      localStorage.removeItem(CONSENT_CONFIG.storageKey);
      const Cookies = getCookiesLib();
      Cookies?.remove('cookie_consent_given');
      
      // Remove individual category cookies
      Object.keys(DEFAULT_CONSENT).forEach(category => {
        Cookies?.remove(`cookie_consent_${category}`);
      });

      setConsent(DEFAULT_CONSENT);
      setHasConsent(false);
    } catch (error) {
      console.error('Error resetting cookie consent:', error);
    }
  };

  // Check if consent has been given for a specific category
  const hasGivenConsent = (category: keyof CookieConsentState): boolean => {
    return consent[category];
  };

  const value: CookieConsentContextType = {
    consent,
    hasConsent,
    isLoading,
    updateConsent,
    acceptAll,
    rejectAll,
    resetConsent,
    hasGivenConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

const defaultCookieConsentValue: CookieConsentContextType = {
  consent: DEFAULT_CONSENT,
  hasConsent: false,
  isLoading: false,
  updateConsent: () => {},
  acceptAll: () => {},
  rejectAll: () => {},
  resetConsent: () => {},
  hasGivenConsent: () => false,
};

// Hook to use cookie consent
export function useCookieConsent(): CookieConsentContextType {
  if (!CookieConsentContext) {
    throw new Error('CookieConsentContext not initialized');
  }
  const context = useContext(CookieConsentContext);
  
  if (!context) {
    return defaultCookieConsentValue;
  }
  
  return context;
}

/**
 * The visitor's analytics decision, outside React. 'undecided' means no valid
 * stored choice (never answered, expired, or an older consent version), which
 * is different from 'declined': only a decliner gets cookieless analytics.
 */
export function getAnalyticsConsentDecision(): 'granted' | 'declined' | 'undecided' {
  if (typeof window === 'undefined') return 'undecided';
  try {
    const storedConsent = localStorage.getItem(CONSENT_CONFIG.storageKey);
    if (!storedConsent) return 'undecided';
    const consentData: CookieConsentData = JSON.parse(storedConsent);
    if (consentData.metadata.expiresAt <= Date.now()) return 'undecided';
    if (consentData.metadata.version !== CONSENT_CONFIG.version) return 'undecided';
    return consentData.consent.analytics ? 'granted' : 'declined';
  } catch {
    return 'undecided';
  }
}

// Utility function to check consent outside of React (for analytics initialization)
export function getCookieConsent(category: keyof CookieConsentState): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const storedConsent = localStorage.getItem(CONSENT_CONFIG.storageKey);
    
    if (!storedConsent) {
      return false;
    }

    const consentData: CookieConsentData = JSON.parse(storedConsent);
    
    // Check if consent is still valid
    if (consentData.metadata.expiresAt <= Date.now()) {
      return false;
    }

    // Check version
    if (consentData.metadata.version !== CONSENT_CONFIG.version) {
      return false;
    }

    return consentData.consent[category];
  } catch (error) {
    console.error('Error checking cookie consent:', error);
    return false;
  }
}

export default CookieConsentProvider;
